import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import api from '../services/api';

// Upcoming Appointment Card with Live Countdown
const UpcomingAppointmentCard = ({ appointment }) => {
  const [timeUntilStart, setTimeUntilStart] = useState('');

  useEffect(() => {
    const updateCountdown = () => {
      const startTime = new Date(appointment.start_time);
      const now = new Date();
      const diffMs = startTime - now;

      if (diffMs <= 0) {
        setTimeUntilStart('Starting now!');
        return;
      }

      const diffSecs = Math.floor(diffMs / 1000);
      const days = Math.floor(diffSecs / 86400);
      const hours = Math.floor((diffSecs % 86400) / 3600);
      const minutes = Math.floor((diffSecs % 3600) / 60);
      const seconds = diffSecs % 60;

      let countdown = '';
      if (days > 0) {
        countdown = `${days}d ${hours}h ${minutes}m`;
      } else if (hours > 0) {
        countdown = `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        countdown = `${minutes}m ${seconds}s`;
      } else {
        countdown = `${seconds}s`;
      }

      setTimeUntilStart(countdown);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [appointment.start_time]);

  const startTime = new Date(appointment.start_time);
  const now = new Date();
  const isPast = startTime < now;

  if (isPast) return null;

  const formatTime = (date) => {
    const dateOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    const timeOptions = { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };
    return `${date.toLocaleDateString('en-US', dateOptions)} at ${date.toLocaleTimeString('en-US', timeOptions)}`;
  };

  return (
    <div className="bg-white border-2 border-indigo-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              📅 Appointment
            </span>
            <span className="text-sm text-gray-600">
              {formatTime(startTime)}
            </span>
            {timeUntilStart && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
                ⏱️ Starts in {timeUntilStart}
              </span>
            )}
          </div>
          <p className="text-gray-900 font-medium mb-1">
            Created by {appointment.creator_name}
          </p>
          <p className="text-gray-700 text-sm">
            {appointment.description.length > 150
              ? `${appointment.description.substring(0, 150)}...`
              : appointment.description}
          </p>
          <div className="mt-2 text-sm text-gray-500">
            {appointment.duration_minutes} minutes • {appointment.participant_count} participant{appointment.participant_count !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { socket, connected } = useSocket();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form states for booking - Load from localStorage or use defaults
  const [focusGoal, setFocusGoal] = useState(() => {
    const saved = localStorage.getItem('vibeyfocus_focusGoal');
    console.log('📖 Loading focusGoal from localStorage:', saved);
    return saved || '';
  });
  const [selectedCategory] = useState(() => {  // eslint-disable-line no-unused-vars
    const saved = localStorage.getItem('vibeyfocus_category');
    console.log('📖 Loading category from localStorage:', saved);
    return saved || 'general_productivity';
  });
  const [sessionDurations, setSessionDurations] = useState(() => {
    const saved = localStorage.getItem('vibeyfocus_durations');
    console.log('📖 Loading durations from localStorage:', saved);

    // Handle backward compatibility with old single-duration format
    const oldDuration = localStorage.getItem('vibeyfocus_duration');
    if (!saved && oldDuration) {
      // Migrate from old format - set only the previously selected duration
      const duration = parseInt(oldDuration);
      console.log('📖 Migrating from old duration format:', duration);
      return {
        25: duration === 25,
        50: duration === 50,
        75: duration === 75,
      };
    }

    return saved ? JSON.parse(saved) : {
      25: true,
      50: true,
      75: true,
    };
  });
  const [sessionTypes, setSessionTypes] = useState(() => {
    const saved = localStorage.getItem('vibeyfocus_sessionTypes');
    console.log('📖 Loading sessionTypes from localStorage:', saved);
    return saved ? JSON.parse(saved) : {
      social: true,
      focusOnly: true,
      silent: true,
    };
  });

  // Matching states
  const [isSearching, setIsSearching] = useState(false);
  const [matchingRequestId, setMatchingRequestId] = useState(null);  // eslint-disable-line no-unused-vars
  const [matchingRequestIds, setMatchingRequestIds] = useState([]);
  const [matchFound, setMatchFound] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);

  // Countdown timer
  const [timeUntilNext, setTimeUntilNext] = useState('');

  const categories = [  // eslint-disable-line no-unused-vars
    { label: 'General', value: 'general_productivity' },
    { label: 'Creative', value: 'creative' },
    { label: 'Spiritual', value: 'spiritual' },
    { label: 'Startup', value: 'startup' }
  ];

  // Save settings to localStorage whenever they change
  useEffect(() => {
    console.log('💾 Saving focusGoal to localStorage:', focusGoal);
    localStorage.setItem('vibeyfocus_focusGoal', focusGoal);
  }, [focusGoal]);

  useEffect(() => {
    console.log('💾 Saving category to localStorage:', selectedCategory);
    localStorage.setItem('vibeyfocus_category', selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    console.log('💾 Saving durations to localStorage:', sessionDurations);
    localStorage.setItem('vibeyfocus_durations', JSON.stringify(sessionDurations));
  }, [sessionDurations]);

  useEffect(() => {
    console.log('💾 Saving sessionTypes to localStorage:', sessionTypes);
    localStorage.setItem('vibeyfocus_sessionTypes', JSON.stringify(sessionTypes));
  }, [sessionTypes]);

  useEffect(() => {
    console.log('📍 Dashboard mounted or user changed');
    fetchUsage();
    fetchAppointments();

    // Check for active session immediately on mount (handles navigation from other pages)
    console.log('🔄 Checking for active session on Dashboard mount...');
    checkForActiveSession();

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('Notification permission:', permission);
      });
    }

    // Check for active session when user returns to this tab/window
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👁️ Dashboard became visible, checking for active session');
        checkForActiveSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const timer = setInterval(() => {
        const now = new Date();
        const isDevelopment = window.location.hostname === 'localhost' ||
                              window.location.hostname === '127.0.0.1' ||
                              window.location.hostname.startsWith('192.168.') ||
                              window.location.hostname.startsWith('10.') ||
                              process.env.NODE_ENV === 'development';

        let nextSession;

        if (isDevelopment) {
          // Development: 30-second intervals
          const currentSeconds = now.getSeconds();
          const nextInterval = Math.ceil((currentSeconds + 1) / 30) * 30;

          nextSession = new Date(now);
          if (nextInterval >= 60) {
            // Move to next minute
            nextSession.setMinutes(now.getMinutes() + 1, 0, 0);
          } else {
            nextSession.setSeconds(nextInterval, 0);
          }
        } else {
          // Production: 15-minute intervals
          const currentMinutes = now.getMinutes();
          let nextInterval = Math.ceil((currentMinutes + 1) / 15) * 15;
          if (nextInterval === 60) nextInterval = 0;

          nextSession = new Date(now);
          if (nextInterval === 0) {
            nextSession.setHours(now.getHours() + 1, 0, 0, 0);
          } else {
            nextSession.setMinutes(nextInterval, 0, 0, 0);
          }

          // Ensure it's in the future
          if (nextSession <= now) {
            nextSession.setMinutes(nextSession.getMinutes() + 15);
          }
        }

        const diffSeconds = Math.floor((nextSession - now) / 1000);
        const minutesUntilNext = Math.floor(diffSeconds / 60);
        const secondsUntilNext = diffSeconds % 60;

        setTimeUntilNext(`${minutesUntilNext}:${secondsUntilNext.toString().padStart(2, '0')}`);
      }, 1000);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); 

  // Socket listeners for matching events
  useEffect(() => {
    if (!socket) return;

    const handleMatchFound = async (data) => {
      console.log('Match found:', data);
      setIsSearching(false);
      setMatchFound(true);
      setError('');
      setInfoMessage('');
      setSessionId(data.sessionId);
      setPartnerInfo({
        id: data.partnerId,
        name: data.partnerName,
        photo: data.partnerPhoto,
        location: data.partnerLocation,
        category: data.partnerCategory,
        description: data.partnerDescription,
        sessionPreferences: data.partnerSessionPreferences,
      });

      // Show browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification('Match Found! 🎉', {
          body: `You've been matched with ${data.partnerName}! Click to join the session.`,
          icon: '/logo192.png', // Use your app icon
          tag: 'match-found', // Replace previous notifications
          requireInteraction: true, // Keep notification visible until clicked
        });

        notification.onclick = () => {
          window.focus();
          notification.close();
          // Navigate to session
          navigate(`/session/${data.sessionId}`, {
            state: {
              partnerInfo: {
                id: data.partnerId,
                name: data.partnerName,
                photo: data.partnerPhoto,
                location: data.partnerLocation,
                category: data.partnerCategory,
                description: data.partnerDescription,
                sessionPreferences: data.partnerSessionPreferences,
              }
            }
          });
        };
      }

      // Cancel all other pending matching requests since we found a match
      if (matchingRequestIds && matchingRequestIds.length > 0) {
        console.log('🧹 Cancelling other pending matching requests');
        for (const requestId of matchingRequestIds) {
          try {
            socket.emit('leave-matching-queue', {
              requestId: requestId,
              userId: user.id,
            });
            await api.delete(`/matching/request/${requestId}`);
            console.log('✅ Cancelled pending request:', requestId);
          } catch (err) {
            console.error('⚠️ Error cancelling pending request:', requestId, err);
            // Continue with other cancellations even if one fails
          }
        }
        setMatchingRequestIds([]);
        setMatchingRequestId(null);
      }
    };

    const handleMatchExpired = (data) => {
      console.log('Match expired:', data);
      setIsSearching(false);
      setError(data.message || 'No match found. Please try again.');
    };

    const handleMatchingError = (data) => {
      console.error('Matching error:', data);
      setIsSearching(false);
      setError(data.error || 'An error occurred while matching.');
    };

    const handleJoinedQueue = (data) => {
      console.log('Joined matching queue:', data);
      // User is now in queue, waiting for scheduled matching
    };

    const handleRequeuedToNextSession = (data) => {
      console.log('Requeued to next session:', data);
      const nextSessionTime = new Date(data.newSessionTime || data.newSessionStart);
      const formattedTime = nextSessionTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
      setError(''); // Clear any previous errors
      setInfoMessage(`No match found for this session. Automatically moved to the next session at ${formattedTime}. Keep waiting!`);
      // Keep searching state active since they're still in queue for next session
    };

    const handleAppointmentJoined = () => {
      // Refresh appointments when user joins one
      fetchAppointments();
    };

    const handleAppointmentLeft = () => {
      // Refresh appointments when user leaves one
      fetchAppointments();
    };

    socket.on('match-found', handleMatchFound);
    socket.on('match-expired', handleMatchExpired);
    socket.on('matching-error', handleMatchingError);
    socket.on('joined-queue', handleJoinedQueue);
    socket.on('requeued-to-next-session', handleRequeuedToNextSession);
    socket.on('participant-joined', handleAppointmentJoined);
    socket.on('participant-left', handleAppointmentLeft);

    return () => {
      socket.off('match-found', handleMatchFound);
      socket.off('match-expired', handleMatchExpired);
      socket.off('matching-error', handleMatchingError);
      socket.off('joined-queue', handleJoinedQueue);
      socket.off('requeued-to-next-session', handleRequeuedToNextSession);
      socket.off('participant-joined', handleAppointmentJoined);
      socket.off('participant-left', handleAppointmentLeft);
    };
  }, [socket, matchingRequestIds, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsage = async () => {
    try {
      if (!user) {
        setUsage({
          subscriptionStatus: 'free',
          minutesRemaining: 125,
          minutesUsed: 0,
          nextResetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        });
        setLoading(false);
        return;
      }

      const response = await api.get('/subscription/usage');
      setUsage(response.data.usage);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      setUsage({
        subscriptionStatus: 'free',
        minutesRemaining: 125,
        minutesUsed: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      if (!user) return;

      const response = await api.get('/appointments', {
        params: {
          participant: user.id
        }
      });

      // Filter to only show appointments the user has joined
      const joinedAppointments = response.data.filter(apt => apt.is_user_participant);
      setUpcomingAppointments(joinedAppointments);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };

  const checkForActiveSession = async () => {
    try {
      if (!user) {
        console.log('⚠️ checkForActiveSession: No user, skipping');
        return;
      }

      console.log('🔍 Checking for active session...');
      const response = await api.get('/sessions/active');
      console.log('📊 Active session response:', response.data);

      if (response.data.hasActiveSession) {
        console.log('✅ Found active session! Setting match state:', response.data);
        setMatchFound(true);
        setSessionId(response.data.sessionId);
        setPartnerInfo({
          id: response.data.partnerId,
          name: response.data.partnerName,
          photo: response.data.partnerPhoto,
          location: response.data.partnerLocation,
          description: response.data.partnerDescription,
        });
        setIsSearching(false);
      } else {
        console.log('ℹ️ No active session found');
      }
    } catch (error) {
      console.error('❌ Failed to check for active session:', error);
    }
  };

  const handleBookSession = async () => {
    // If match is already found, enter the room
    if (matchFound && sessionId) {
      navigate(`/session/${sessionId}`, {
        state: { partnerInfo }
      });
      return;
    }

    // If already searching, do nothing
    if (isSearching) {
      return;
    }

    // Check if user is logged in
    if (!user) {
      setError('Please log in to book a session');
      return;
    }

    // Check if email is verified (skip in development)
    const isDevelopment = window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1' ||
                          window.location.hostname.startsWith('192.168.') ||
                          window.location.hostname.startsWith('10.') ||
                          process.env.NODE_ENV === 'development';

    if (!isDevelopment && !user.verified) {
      setError('Please verify your email before booking a session. Check your inbox for the verification link.');
      return;
    }

    // Category selection disabled for MVP
    // if (!selectedCategory) {
    //   setError('Please select a category before booking a session');
    //   return;
    // }

    // Require at least one session type selection
    if (!sessionTypes.social && !sessionTypes.silent && !sessionTypes.focusOnly) {
      setError('Please select at least one session type before booking a session');
      return;
    }

    // Require at least one duration selection
    if (!sessionDurations[25] && !sessionDurations[50] && !sessionDurations[75]) {
      setError('Please select at least one session duration before booking a session');
      return;
    }

    if (usage?.subscriptionStatus === 'free' && usage?.minutesRemaining === 0) {
      navigate('/subscription');
      return;
    }

    if (!connected) {
      setError('Not connected to server. Please refresh and try again.');
      return;
    }

    setError('');
    setInfoMessage('');
    setIsSearching(true);

    try {
      // Map session style checkboxes to backend fields
      const quietMode = false; // Camera is always on

      // Set neutral behavior - users will decide during the session based on mutual preference
      // All users are "focus-only" by default (neutral stance)
      const talkDuringSession = false;
      const dontTalkMiddleSession = false;

      // Checkboxes indicate what types of partners they'll accept
      const acceptSocialSessions = sessionTypes.social;
      const acceptSilentSessions = sessionTypes.silent;
      const acceptFocusOnlySessions = sessionTypes.focusOnly;

      // Calculate requested start time
      const now = new Date();
      const isDevelopment = window.location.hostname === 'localhost' ||
                            window.location.hostname === '127.0.0.1' ||
                            window.location.hostname.startsWith('192.168.') ||
                            window.location.hostname.startsWith('10.') ||
                            process.env.NODE_ENV === 'development';

      let requestedTime;

      if (isDevelopment) {
        // Development: 30-second intervals (no grace period)
        const currentSeconds = now.getSeconds();
        const nextInterval = Math.floor(currentSeconds / 30) * 30;

        requestedTime = new Date(now);
        requestedTime.setSeconds(nextInterval, 0);

        // If we're in the second half of the interval, move to next
        if (currentSeconds % 30 >= 15) {
          requestedTime.setSeconds(requestedTime.getSeconds() + 30);
        }
      } else {
        // Production: 15-minute intervals with 3-minute grace period
        const currentMinutes = now.getMinutes();
        let nextInterval;
        const minutesIntoInterval = currentMinutes % 15;

        if (minutesIntoInterval < 3) {
          // Within 3 minutes of session start (grace period), use current interval
          nextInterval = Math.floor(currentMinutes / 15) * 15;
        } else {
          // Past the 3-minute grace period, go to next interval
          nextInterval = (Math.floor(currentMinutes / 15) + 1) * 15;
        }

        requestedTime = new Date(now);
        requestedTime.setMinutes(nextInterval % 60, 0, 0);

        // If nextInterval is 60 or more, we need to add an hour
        if (nextInterval >= 60) {
          requestedTime.setHours(requestedTime.getHours() + 1);
        }

        // Check if the calculated time is in the past (shouldn't happen, but just in case)
        if (requestedTime <= now) {
          // Move to next interval
          nextInterval = (Math.floor(currentMinutes / 15) + 1) * 15;
          requestedTime.setMinutes(nextInterval % 60, 0, 0);
          if (nextInterval >= 60) {
            requestedTime.setHours(requestedTime.getHours() + 1);
          }
        }
      }

      // Use toISOString() for proper UTC conversion
      const requestedTimeString = requestedTime.toISOString();

      console.log('Calculated start time:', {
        now: now.toString(),
        nowUTC: now.toISOString(),
        isDevelopment,
        requestedTime: requestedTime.toString(),
        requestedTimeUTC: requestedTimeString
      });

      // Get selected durations
      const selectedDurations = Object.keys(sessionDurations)
        .filter(duration => sessionDurations[duration])
        .map(duration => parseInt(duration));

      console.log('📊 Session durations state:', sessionDurations);
      console.log('📊 Selected durations to create requests for:', selectedDurations);

      if (selectedDurations.length === 0) {
        setError('Please select at least one session duration');
        setIsSearching(false);
        return;
      }

      // Create matching requests for each selected duration
      const requestIds = [];
      for (const duration of selectedDurations) {
        try {
          console.log(`📝 Creating matching request for ${duration} minutes...`);

          const response = await api.post('/matching/request', {
            category: 'general_productivity', // Default category for all users
            description: focusGoal || 'Focus session',
            acceptableCategories: ['general_productivity', 'creative', 'spiritual', 'startup'], // Accept all categories
            sessionLength: duration,
            requestedStartTime: requestedTimeString,
            quietMode,
            talkDuringSession,
            dontMatchQuietMode: false,
            dontTalkMiddleSession,
            acceptSocialSessions,
            acceptSilentSessions,
            acceptFocusOnlySessions,
          });

          const { request } = response.data;
          requestIds.push(request.id);
          console.log(`✅ Created request ${request.id} for ${duration} minutes`);

          // Join matching queue via Socket.io for each request
          socket.emit('join-matching-queue', {
            requestId: request.id,
            userId: user.id,
            userName: user.first_name,
            userPhoto: user.profile_photo,
            userLocation: user.location,
            category: selectedCategory || 'General',
            description: focusGoal || 'Focus session',
            quietMode,
            talkDuringSession,
          });

          console.log(`🔔 Joined matching queue with request: ${request.id}`);
        } catch (err) {
          console.error(`❌ Failed to create request for ${duration} minutes:`, err);
          throw err; // Re-throw to stop the process if any request fails
        }
      }

      console.log(`✅ Successfully created ${requestIds.length} matching requests:`, requestIds);

      // Store all request IDs
      setMatchingRequestIds(requestIds);
      setMatchingRequestId(requestIds[0]); // Keep for backward compatibility
    } catch (err) {
      console.error('Error creating matching request:', err);
      setError(err.response?.data?.error || 'Failed to start matching. Please try again.');
      setIsSearching(false);
    }
  };

  const handleCancelMatching = async () => {
    console.log('🚫 Cancel button clicked, matchingRequestIds:', matchingRequestIds);

    if (!matchingRequestIds || matchingRequestIds.length === 0) {
      console.log('❌ No matching request IDs found');
      setError('No active matching request to cancel');
      setIsSearching(false);
      return;
    }

    try {
      console.log('📡 Cancelling all matching requests');

      // Cancel all matching requests
      for (const requestId of matchingRequestIds) {
        console.log('📡 Emitting leave-matching-queue socket event for:', requestId);
        // Leave matching queue via Socket.io
        socket.emit('leave-matching-queue', {
          requestId: requestId,
          userId: user.id,
        });

        console.log('🔄 Calling API to cancel request:', requestId);
        // Cancel via API
        await api.delete(`/matching/request/${requestId}`);
      }

      console.log('✅ Successfully cancelled all matching requests');
      setIsSearching(false);
      setMatchingRequestId(null);
      setMatchingRequestIds([]);
      setError('');
    } catch (err) {
      console.error('❌ Error cancelling matching:', err);
      setError('Failed to cancel. Please try again.');
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    setVerificationMessage('');

    try {
      await api.post('/auth/resend-verification');
      setVerificationMessage('Verification email sent! Please check your inbox.');
    } catch (err) {
      console.error('Error resending verification:', err);
      setVerificationMessage(err.response?.data?.error || 'Failed to resend verification email. Please try again.');
    } finally {
      setResendingVerification(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md max-w-2xl mx-auto">
            {error}
          </div>
        )}

        {/* Info Message (for requeue notifications) */}
        {infoMessage && (
          <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md max-w-2xl mx-auto">
            {infoMessage}
          </div>
        )}

        {/* Email Verification Banner */}
        {user && !user.verified && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 px-6 py-4 rounded-md max-w-2xl mx-auto">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Email Verification Required
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Please verify your email address to book sessions. Check your inbox for the verification link.
                  </p>
                  {verificationMessage && (
                    <p className="mt-2 font-medium">
                      {verificationMessage}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleResendVerification}
                    disabled={resendingVerification}
                    className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendingVerification ? 'Sending...' : 'Resend verification email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

          {/* Left Side - Focus Input and Categories */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Focus Input */}
              <div>
                <label className="block text-xl font-medium text-gray-800 mb-4">
                  I will <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-cyan-400">focus</span> on
                </label>
                <textarea
                  value={focusGoal}
                  onChange={(e) => setFocusGoal(e.target.value)}
                  placeholder="What will you work on?"
                  className="w-full px-6 py-4 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-400 resize-none text-gray-700 placeholder-gray-400"
                  rows="6"
                  style={{
                    borderImage: 'linear-gradient(135deg, #a855f7 0%, #06b6d4 100%) 1',
                  }}
                />
              </div>

              {/* Category Buttons - DISABLED FOR MVP */}
              {/* <div className="space-y-3">
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    className={`w-full px-6 py-3 rounded-lg text-base font-normal transition-all ${
                      selectedCategory === cat.value
                        ? 'bg-gray-900 text-white shadow-md'
                        : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400 hover:shadow-sm'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div> */}
            </div>
          </div>

          {/* Center - Large Circle Button */}
          <div className="lg:col-span-1 flex items-center justify-center">
            {isSearching ? (
              // Searching State
              <div className="relative w-80 h-80 rounded-full bg-gradient-to-br from-purple-500 via-fuchsia-500 to-cyan-400 shadow-2xl flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-4"></div>
                <span className="text-white text-2xl font-semibold mb-2">Finding Match...</span>
                <button
                  onClick={handleCancelMatching}
                  className="mt-4 px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
                >
                  Cancel
                </button>
                {/* Pulse animation */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500 to-cyan-400 animate-pulse opacity-30 -z-10"></div>
              </div>
            ) : matchFound ? (
              // Matched State
              <button
                onClick={handleBookSession}
                className="relative w-80 h-80 rounded-full bg-gradient-to-br from-green-400 via-emerald-500 to-teal-400 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 flex flex-col items-center justify-center"
              >
                <div className="text-6xl mb-4">✓</div>
                <span className="text-white text-3xl font-semibold mb-2">Match Found!</span>
                <span className="text-white text-lg opacity-90">Click to Enter Room</span>
                {/* Pulse animation */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-green-400 to-teal-400 animate-pulse opacity-30 -z-10"></div>
              </button>
            ) : (
              // Default State
              <button
                onClick={handleBookSession}
                className="relative w-80 h-80 rounded-full bg-gradient-to-br from-purple-500 via-fuchsia-500 to-cyan-400 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 flex flex-col items-center justify-center"
              >
                <span className="text-white text-3xl font-semibold mb-6">Book Session</span>
                <div className="text-white text-center">
                  <div className="text-4xl font-bold mb-1">{timeUntilNext}</div>
                  <div className="text-base opacity-90">until next session</div>
                </div>
                {/* Subtle pulse animation */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500 to-cyan-400 animate-pulse opacity-30 -z-10"></div>
              </button>
            )}
          </div>

          {/* Right Side - Session Settings */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              {/* Duration */}
              <div className="mb-8">
                <h3 className="text-base font-bold text-gray-900 mb-4">Session Duration (all I'm open to)</h3>
                <div className="space-y-3">
                  {[25, 50, 75].map(duration => (
                    <label key={duration} className="flex items-center cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={sessionDurations[duration]}
                        onChange={(e) => setSessionDurations({...sessionDurations, [duration]: e.target.checked})}
                        className="w-4 h-4 text-gray-900 border-gray-300 focus:ring-gray-900 rounded"
                      />
                      <div className="ml-3">
                        <div className="font-medium text-gray-900">{duration} minutes</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Session Type */}
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-4">Session Type (all I'm open to)</h3>
                <div className="space-y-3">
                  <label className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={sessionTypes.social}
                      onChange={(e) => setSessionTypes({...sessionTypes, social: e.target.checked})}
                      className="w-4 h-4 text-gray-900 border-gray-300 mt-0.5 focus:ring-gray-900 rounded"
                    />
                    <div className="ml-3">
                      <div className="font-medium text-gray-900">Social Session</div>
                      <p className="text-sm text-gray-500">Open to chatting and taking breaks together during the session</p>
                    </div>
                  </label>

                  <label className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={sessionTypes.focusOnly}
                      onChange={(e) => setSessionTypes({...sessionTypes, focusOnly: e.target.checked})}
                      className="w-4 h-4 text-gray-900 border-gray-300 mt-0.5 focus:ring-gray-900 rounded"
                    />
                    <div className="ml-3">
                      <div className="font-medium text-gray-900">Focus Only</div>
                      <p className="text-sm text-gray-500">Talk before and after the session, stay focused during</p>
                    </div>
                  </label>

                  <label className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={sessionTypes.silent}
                      onChange={(e) => setSessionTypes({...sessionTypes, silent: e.target.checked})}
                      className="w-4 h-4 text-gray-900 border-gray-300 mt-0.5 focus:ring-gray-900 rounded"
                    />
                    <div className="ml-3">
                      <div className="font-medium text-gray-900">Silent Focus</div>
                      <p className="text-sm text-gray-500">Camera on for accountability, no talking at all</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Sessions Section */}
        {upcomingAppointments.length > 0 && (
          <div className="mt-12 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Upcoming Sessions</h2>
            <div className="space-y-4">
              {upcomingAppointments.map((appointment) => {
                return <UpcomingAppointmentCard key={appointment.id} appointment={appointment} />;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
